
import { api } from '../core';
import { IntegrationApp } from '../../types';
import { MOCK_INTEGRATIONS } from '../mockData';
import { localDB } from '../helpers';

// Map API ServiceOutputVM to UI IntegrationApp
const mapToUI = (apiSvc: any): IntegrationApp => {
    let config: any = {};
    try { config = JSON.parse(apiSvc.config || '{}'); } catch(e) {}

    return {
        id: apiSvc.id,
        name: apiSvc.name,
        description: apiSvc.description,
        icon: config.icon || 'fa-plug',
        color: config.color || 'from-slate-500 to-slate-700',
        connected: apiSvc.isActive, // Mapping active state to connected
        config: config.settings || {},
        permissions: config.permissions || [],
        stats: {
            requests: apiSvc.usageCount || 0,
            timeSaved: 0 // Not in Service schema, maybe calculate?
        },
        hidden: false
    };
};

export const IntegrationsService = {
  getAll: async (): Promise<IntegrationApp[]> => {
    return api.execute<IntegrationApp[]>(
      '/api/V1/Service/GetAllByLanguage',
      async () => localDB.get<IntegrationApp[]>('lahja_integrations', MOCK_INTEGRATIONS),
      { mockDelay: 400 }
    ).then(res => {
        // Filter for services that are actually integrations (e.g. type='integration' or category='app')
        // For now, assuming if it comes from server, we check a flag. 
        // If it matches MOCK structure, return as is.
        if (Array.isArray(res) && res.length > 0 && 'endpointUrl' in res[0]) {
            // Filter by type if possible, or just map all for now
            return res
                .filter((s: any) => {
                    let c = {}; 
                    try { c = JSON.parse(s.config || '{}'); } catch(e) {}
                    return (c as any).type === 'integration';
                })
                .map(mapToUI);
        }
        return res as IntegrationApp[];
    });
  },

  toggleConnection: async (id: string, connected: boolean): Promise<boolean> => {
    // Fetch current state first
    const current = await api.get<any>(`/api/V1/Service/Get/${id}`).catch(() => null);
    
    if (current && current.id) {
         // Map to UpdateVM
         const updateBody = {
             ...current, // Copy existing
             isActive: connected
         };
         
         return api.execute<boolean>(
            `/api/V1/Service/Update/${id}`,
            async () => {
                const apps = localDB.get<IntegrationApp[]>('lahja_integrations', MOCK_INTEGRATIONS);
                const updatedApps = apps.map(a => a.id === id ? { ...a, connected } : a);
                localDB.set('lahja_integrations', updatedApps);
                return true;
            },
            { method: 'PUT', body: { id, body: updateBody } }
         ).then(() => true);
    }

    // Fallback for local mode directly
    const apps = localDB.get<IntegrationApp[]>('lahja_integrations', MOCK_INTEGRATIONS);
    const updatedApps = apps.map(a => a.id === id ? { ...a, connected } : a);
    localDB.set('lahja_integrations', updatedApps);
    return true;
  },

  updateConfig: async (id: string, config: any): Promise<boolean> => {
      // Similar to toggle, fetch then update 'config' JSON string
      const current = await api.get<any>(`/api/V1/Service/Get/${id}`).catch(() => null);
      
      if (current && current.id) {
         let currentConfig = {};
         try { currentConfig = JSON.parse(current.config || '{}'); } catch(e) {}
         
         const newConfig = {
             ...currentConfig,
             settings: config // Update settings part
         };

         const updateBody = {
             ...current,
             config: JSON.stringify(newConfig)
         };

         return api.execute<boolean>(
            `/api/V1/Service/Update/${id}`,
            async () => {
                const apps = localDB.get<IntegrationApp[]>('lahja_integrations', MOCK_INTEGRATIONS);
                const updatedApps = apps.map(a => a.id === id ? { ...a, config } : a);
                localDB.set('lahja_integrations', updatedApps);
                return true;
            },
            { method: 'PUT', body: { id, body: updateBody } }
         ).then(() => true);
      }
      
      const apps = localDB.get<IntegrationApp[]>('lahja_integrations', MOCK_INTEGRATIONS);
      const updatedApps = apps.map(a => a.id === id ? { ...a, config } : a);
      localDB.set('lahja_integrations', updatedApps);
      return true;
  }
};
